'use client';

import { Children, type ReactElement, cloneElement, useState, type MouseEvent } from 'react';

import { AppDownloadBottomSheet } from '~/components';
import { useEnvironmentInfo } from '~/hooks';

type Props = {
  children: ReactElement;
  /** 이 prop이 있을 경우, 이벤트가 발생한 current target에 className이 있을 경우에만 팝업을 보여준다 (CMS뷰어 컨텐츠 중 클릭커블한 요소에서 사용하기 위해) */
  requiredClassName?: string;
};

/** 웹 환경에서 클릭시 기존 클릭 이벤트를 실행하지 않고 앱 다운로드 팝업을 보여준다. */
export const AppDownloadTrigger = ({ children, requiredClassName }: Props) => {
  const [visible, setVisible] = useState<'BOTTOM_SHEET'>();
  const { isWebview } = useEnvironmentInfo();

  const child = Children.only(children);

  if (isWebview) return child;

  return (
    <>
      {cloneElement(child as ReactElement<{ onClick?: (e: MouseEvent<HTMLElement>) => void }>, {
        onClick: (event: MouseEvent<HTMLElement>) => {
          event.preventDefault();
          event.stopPropagation();

          if (requiredClassName) {
            const hasRequiredClassName = hasParentWithClass(event.target as HTMLElement, requiredClassName);
            if (!hasRequiredClassName) return;
          }

          setVisible('BOTTOM_SHEET');
        },
      })}

      <AppDownloadBottomSheet isOpen={visible === 'BOTTOM_SHEET'} onClose={() => setVisible(undefined)} />
    </>
  );
};

const hasParentWithClass = (element: HTMLElement | null, className: string): boolean => {
  if (!element || !element.classList) {
    return false;
  }

  if (element.classList.contains(className)) {
    return true;
  }

  return hasParentWithClass(element.parentElement, className);
};
